import React, { useState, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  FormLabel
} from '@material-ui/core';
import {
  TextField,
  DateField,
  RichTextField,
  SaveButton,
  useNotify,
  useRefresh
} from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import UserActivityApi from '../../../core/apis/UserActivityApi';
import UserActivityForm from './UserActivityForm';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';

const useStyles = makeStyles({
  outlined: {
    border: '1px solid #E0E0E0',
    background: 'white',
    'border-radius': '2px'
  },
  button: {
    color: '#2962FF'
  }
});

const UserActivityDetailDialog = props => {
  const { record, handleClose, open, userId, operation } = props;
  const [activityFormValues, setActivityFormValues] = useState();
  const [activityForm, setActivityForm] = useState();
  const [isCreating, setIsCreating] = useState(false);
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const refresh = useRefresh();
  const classes = useStyles();
  const detailsTitle = record
    ? record.activityType === 'EMAIL'
      ? 'Email Details'
      : record.activityType === 'FILE_ACCESSED'
      ? 'File Accessed Details'
      : record.activityType === 'NOTE'
      ? 'Note Details'
      : ''
    : 'Add Note';

  const handleSaveClick = useCallback(
    event => {
      activityForm && activityForm.submit(event); //NOSONAR cwe
    },
    [activityForm]
  );

  const closeDialog = useCallback(() => {
    setActivityForm(null);
    handleClose();
  }, [setActivityForm, handleClose]);

  const handleSubmit = useCallback(
    async value => {
      try {
        setIsCreating(true);
        const note = { ...value, userId: userId };
        if (note.time) {
          const timeParts = note.time.split(':');
          note.dateTime = moment(note.dateTime)
            .set('hour', timeParts[0])
            .set('minute', timeParts[1])
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
        } else {
          note.dateTime = moment(note.dateTime).format(
            'YYYY-MM-DDTHH:mm:ss.SSS[Z]'
          );
        }
        delete note.time;
        await UserActivityApi.add(note);
        notify('ra.notification.created');
        setIsCreating(false);
        handleClose();
        refresh();
      } catch (e) {
        setIsCreating(false);
        notifyServerError(e);
      }
    },
    [handleClose, notify, refresh, userId, notifyServerError]
  );

  return (
    <Dialog
      open={open}
      aria-label="Details"
      maxWidth="md"
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        closeDialog();
      }}
    >
      <DialogTitleWithCloseButton onClose={closeDialog}>
        {detailsTitle}
      </DialogTitleWithCloseButton>
      <DialogContent>
        {operation === 'Create' ||
        (record && record.activityType === 'NOTE') ? (
          <UserActivityForm
            {...props}
            record={record ?? {}}
            setActivityForm={setActivityForm}
            activityForm={activityForm}
            setActivityFormValues={setActivityFormValues}
            activityFormValues={activityFormValues}
            handleSubmit={handleSubmit}
          />
        ) : record && record.activityType === 'FILE_ACCESSED' ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormLabel>Date & Time</FormLabel>
                </Grid>
                <Grid item xs={12} className={classes.outlined}>
                  <DateField record={record} source="dateTime" showTime />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormLabel>Subject/Document Title</FormLabel>
                </Grid>
                <Grid item xs={12} className={classes.outlined}>
                  <TextField record={record} source="documentActivity.title" />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormLabel>Batch ID</FormLabel>
                </Grid>
                <Grid item xs={12} className={classes.outlined}>
                  <TextField
                    record={record}
                    source="documentActivity.batchId"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormLabel>Document ID</FormLabel>
                </Grid>
                <Grid item xs={12} className={classes.outlined}>
                  <TextField
                    record={record}
                    source="documentActivity.reportId"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormLabel>Fund ID</FormLabel>
                </Grid>
                <Grid item xs={12} className={classes.outlined}>
                  <TextField record={record} source="documentActivity.fundId" />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormLabel>Investor ID</FormLabel>
                </Grid>
                <Grid item xs={12} className={classes.outlined}>
                  <TextField
                    record={record}
                    source="documentActivity.investorId"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormLabel>Date & Time</FormLabel>
                </Grid>
                <Grid item xs={12} className={classes.outlined}>
                  <DateField record={record} source="dateTime" showTime />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormLabel>Subject</FormLabel>
                </Grid>
                <Grid item xs={12} className={classes.outlined}>
                  <TextField record={record} source="subject" />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <FormLabel>Body</FormLabel>
                </Grid>
                <Grid item xs={12} className={classes.outlined}>
                  <RichTextField
                    record={record}
                    source="details"
                    onClick={e => {
                      if (e.target.href) {
                        e.target.target = '_blank';
                        e.target.rel = 'noreferrer noopener';
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions style={{ borderTop: `1px solid rgba(0, 0, 0, 0.12)` }}>
        {(operation === 'Create' ||
          (record && record.activityType === 'NOTE')) && (
          <SaveButton
            style={{ minWidth: '82px' }}
            saving={isCreating}
            handleSubmitWithRedirect={handleSaveClick}
            label={operation === 'Create' ? 'Add' : 'Save'}
            icon={<></>}
            redirect={false}
          />
        )}
        <Button onClick={closeDialog} variant="outlined" color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserActivityDetailDialog;
